import { useCallback, useEffect, useState } from "react";

import { type TosMetadata } from "../../../../gql-generated/types";

interface UseTosModalStateProps {
    terms?: TosMetadata["terms"];
    visible: boolean;
    tosMetadata?: TosMetadata;
    accepted: boolean;
    onAccept: () => Promise<void>;
    initialStepKey?: string;
}

interface UseTosModalStateReturn {
    currentStep: number;
    completedSteps: Set<number>;
    isMultiStep: boolean;
    totalSteps: number;
    isLastStep: boolean;
    isCurrentStepCompleted: boolean;
    handleStepNavigation: (stepIndex: number) => void;
    handleNextStep: () => Promise<void>;
    resetState: () => void;
    markStepCompleted: (step: number) => void;
}

export const useTosModalState = ({
    terms,
    visible,
    tosMetadata,
    accepted,
    onAccept,
    initialStepKey,
}: UseTosModalStateProps): UseTosModalStateReturn => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

    const isMultiStep = Boolean(terms && terms.length > 1);
    const totalSteps = isMultiStep ? terms?.length ?? 0 : 1;
    const isLastStep = currentStep === totalSteps - 1;
    const isCurrentStepCompleted = completedSteps.has(currentStep);

    const getInitialStep = useCallback(() => {
        if (!initialStepKey || !terms || terms.length === 0) {
            return 0;
        }
        const stepIndex = terms.findIndex((term) => term.key === initialStepKey);
        return stepIndex === -1 ? 0 : stepIndex;
    }, [initialStepKey, terms]);

    const resetState = useCallback(() => {
        setCurrentStep(getInitialStep());
        setCompletedSteps(new Set());
    }, [getInitialStep]);

    useEffect(() => {
        if (visible) {
            resetState();
        }
    }, [visible, tosMetadata, resetState]);

    const handleStepNavigation = useCallback(
        (stepIndex: number) => {
            if (accepted) {
                setCurrentStep(stepIndex);
                return;
            }

            if (stepIndex <= currentStep || completedSteps.has(stepIndex)) {
                setCurrentStep(stepIndex);
            }
        },
        [currentStep, completedSteps, accepted],
    );

    const handleNextStep = useCallback(async () => {
        if (isLastStep && isCurrentStepCompleted) {
            if (!accepted) {
                await onAccept();
            }
        } else if (isCurrentStepCompleted) {
            setCurrentStep((prev) => prev + 1);
        }
    }, [isLastStep, isCurrentStepCompleted, onAccept, accepted]);

    const markStepCompleted = useCallback((step: number) => {
        setCompletedSteps((prev) => new Set([...prev, step]));
    }, []);

    return {
        currentStep,
        completedSteps,
        isMultiStep,
        totalSteps,
        isLastStep,
        isCurrentStepCompleted,
        handleStepNavigation,
        handleNextStep,
        resetState,
        markStepCompleted,
    };
};
